import { Drawer, IconButton, Stack, useMediaQuery } from '@mui/material';
import React from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext'
import MenuIcon from '@mui/icons-material/Menu';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import Box from "@mui/material/Box";

type NavItem =
  | { label: string; to: string; href?: never }
  | { label: string; href: string; to?: never };

const Header: React.FC = () => {
  const { isAuthenticated, logoutUser } = useAuth();
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width:900px)');

  const navItems: NavItem[] = [
    { label: 'Features', to: '/features' },
    { label: 'Case Studies', to: '/case-studies' },
    { label: 'Documentation', href: '/docs' },
    { label: 'Downloads', to: '/downloads' },
    { label: 'Support', to: '/support' },
    { label: 'Account', to: '/account' },
  ];

  const toggleDrawer = (open: boolean) => {
    setDrawerOpen(open);
  };

  const handleLogout = () => {
    logoutUser();
    navigate('/');
    toggleDrawer(false); // Ensure drawer closes on logout
  };

  const navButtonSx = (to?: string) => ({
    color: to && location.pathname.startsWith(to) ? 'primary.main' : 'text.primary',
    px: 1.5,
    py: 1,
    fontSize: '0.9rem',
    '&:hover': {
      bgcolor: 'rgba(19, 92, 114, 0.08)',
    },
  });

  const renderNavButton = (item: NavItem) => {
    if ('href' in item) {
      return (
        <Button key={item.label} href={item.href} sx={navButtonSx()}>
          {item.label}
        </Button>
      );
    }

    return (
      <Button key={item.label} component={Link} to={item.to} sx={navButtonSx(item.to)}>
        {item.label}
      </Button>
    );
  };

  const renderDrawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: 300, p: 2.5 }}>
      <Box component={Link} to="/" onClick={() => toggleDrawer(false)} sx={{ color: 'inherit', textDecoration: 'none', mb: 3 }}>
        <Typography variant="h6">FEMORPH</Typography>
        <Typography variant="body2" color="text.secondary">Software Portal</Typography>
      </Box>
      <Stack spacing={0.5} alignItems="stretch">
        {navItems.map((item) => (
          'href' in item ? (
            <Button key={item.label} href={item.href} onClick={() => toggleDrawer(false)} sx={{ justifyContent: 'flex-start' }}>
              {item.label}
            </Button>
          ) : (
            <Button key={item.label} component={Link} to={item.to} onClick={() => toggleDrawer(false)} sx={{ justifyContent: 'flex-start' }}>
              {item.label}
            </Button>
          )
        ))}
      </Stack>
      {isAuthenticated ? (
        <Button variant="outlined" startIcon={<LogoutIcon />} sx={{ mt: 'auto' }} onClick={handleLogout}>
          Logout
        </Button>
      ) : (
        <Button
          variant="contained"
          startIcon={<LoginIcon />}
          sx={{ mt: 'auto' }}
          onClick={() => {
            toggleDrawer(false);
            navigate('/login');
          }}
        >
          Login
        </Button>
      )}
    </Box>
  );

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: 'rgba(255, 255, 255, 0.92)',
        color: 'text.primary',
        borderBottom: '1px solid',
        borderColor: 'divider',
        backdropFilter: 'blur(12px)',
      }}
    >
      <Toolbar sx={{ minHeight: { xs: 68, md: 78 }, px: { xs: 2, md: 4 }, gap: 1 }}>
        <Link
          to="/"
          style={{
            minHeight: 64,
            display: 'flex',
            alignItems: 'center',
            color: 'inherit',
            textDecoration: 'none',
            flexGrow: 1,
          }}
        >
          <img
            className="logo"
            src="/logo192.png"
            alt="Logo"
            style={{ height: 46, marginRight: '10px' }}
          />
          <Box>
            <Typography sx={{ px: 1, lineHeight: 1 }} variant="h5" color="inherit" noWrap>
              FEMORPH
            </Typography>
            <Typography sx={{ px: 1, display: { xs: 'none', sm: 'block' } }} variant="caption" color="text.secondary" noWrap>
              Software Portal
            </Typography>
          </Box>
        </Link>
        <IconButton
          edge="start"
          color="inherit"
          aria-label="menu"
          onClick={() => toggleDrawer(true)}
          sx={{ mr: 2, display: { xs: 'block', sm: 'block', md: 'none' } }}
        >
          <MenuIcon />
        </IconButton>
        {!isMobile && (
          <Stack direction="row" alignItems="center" spacing={0.25}>
            {navItems.map(renderNavButton)}
            {isAuthenticated ? (
              <Button variant="outlined" startIcon={<LogoutIcon />} sx={{ ml: 1, minWidth: 96 }} onClick={handleLogout}>
                Logout
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={<LoginIcon />}
                sx={{ ml: 1, minWidth: 92 }}
                onClick={() => navigate('/login')}
              >
                Login
              </Button>
            )}
          </Stack>
        )}
      </Toolbar>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => toggleDrawer(false)}
      >
        {renderDrawerContent}
      </Drawer>
    </AppBar>
  );
};

export default Header;
